/*
 * Copyright (c) 2020.  Rikesh Lal Shrestha
 * All Rights Reserved
 */

import React, { createContext, useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';

import {
  API_URL,
  ERROR_MESSAGE,
  SYSTEM_SUPPORT_COMPANY_DATA,
  SYSTEM_USER_ID,
  SYSTEM_USER_TYPE,
  SYSTEM_ENCRYPTION_KEY,
} from '../../../constants/appConfig';
import history from '../../../utils/history';
import Toast from '../../../components/shared/Toast';
import { clearLocalStorage, getLocalStorage, setLocalStorage } from '../../../utils/storageUtil';
import { isAdminAuthenticated } from '../../../utils/jwtUtil';
import { adminAppUnregisterFirebase } from '../../../pushnotification';
import { systemLoginLocalStorage, systemLogoutLocalStorage } from '../../../utils/authUtil';
import { getCurrentTimeZone } from '../../../utils/commonUtil';

const SystemAuthContext = createContext({});

const SystemAuthProvider = props => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(getLocalStorage('user') || {});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState();
  const [fields, setFields] = useState({ userId: '', password: '', accessCode: '' });
  const [sideBarMenu, setSideBarMenu] = useState('users');
  const [primaryColor, setPrimaryColor] = useState('#466FEE');
  const [secondaryColor, setSecondaryColor] = useState('#3CD4A0');

  // System App
  const [systemUserId, setSystemUserId] = useState(getLocalStorage(SYSTEM_USER_ID) || {});
  const [systemUserType, setSystemUserType] = useState(getLocalStorage(SYSTEM_USER_TYPE) || {});
  const [systemAuthenticated, setSystemAuthenticated] = useState(isAdminAuthenticated() || false);
  const [systemSupportData, setSystemSupportData] = useState(
    getLocalStorage(SYSTEM_SUPPORT_COMPANY_DATA)
  );
  const [systemCompanyDataLoading, setSystemCompanyDataLoading] = useState(false);
  const [systemEncryptedKey, setSystemEncryptedKey] = useState(
    getLocalStorage(SYSTEM_ENCRYPTION_KEY || {})
  );

  const [unreadSystemSupportCount, setUnreadSystemSupportCount] = useState(0);
  const state = {
    user,
    loading,
    systemUserId,
    systemUserType,
    systemAuthenticated,
    systemSupportData,
    systemCompanyDataLoading,
    systemEncryptedKey,
    unreadSystemSupportCount,
  };

  const cleanSystemAuthContextClean = () => {
    setLoading(false);
    setMessage(undefined);
    setFields({ userId: '', password: '', accessCode: '' });
  };

  const systemLoginSuccess = responseData => {
    systemLoginLocalStorage(responseData);

    setSystemUserId(responseData.userId);
    setSystemUserType('SYSTEM');
    setMessage(undefined);
    setSystemAuthenticated(true);
    setSideBarMenu('client');
    setLoading(false);
    setUnreadSystemSupportCount(0);
    setSystemEncryptedKey(responseData?.encryptionKey);
    // setFields({ userId: '', password: '', accessCode: '' });
    history.push('/system/dashboard');
  };

  const systemRequestCompanyEmailVerification = formData => {
    setLoading(true);
    return axios
      .post(API_URL + '/atom-admin/v1/register/request/email-verification', formData, {
        headers: {
          'X-TIME-ZONE': getCurrentTimeZone(),
        },
      })
      .then(response => {
        if (response.status === 200) {
          Toast(
            `Activation key has been successfully sent to ${formData.email}. Please check your mail.`,
            'SUCCESS'
          );
          setLoading(false);
          return response;
        }
      })
      .catch(error => {
        setLoading(false);
        setMessage(error.response ? error.response.data.message : ERROR_MESSAGE);
      });
  };

  const systemLogin = ({ userId, password }) => {
    setLoading(true);
    return axios
      .post(
        API_URL + '/atom-admin/v1/auths',
        { userId, password },
        {
          headers: {
            'X-TIME-ZONE': getCurrentTimeZone(),
          },
        }
      )
      .then(response => {
        if (response.status === 200) {
          systemLoginSuccess(response.data.data);
          return response.data;
        }
      })
      .catch(error => {
        setLoading(false);
        setMessage(error?.response?.data?.message ? error.response.data.message : ERROR_MESSAGE);
      });
  };

  const systemLogout = () => {
    systemLogoutLocalStorage();

    setFields({ userId: '', password: '', accessCode: '' });
    setSystemUserType(undefined);
    setUser({});
    setSystemAuthenticated(false);
    setSystemEncryptedKey({});

    // history.push(`/system/login`);
    systemSupportCompanyDataClean();

    // dispatch({
    //   type: 'LOG_OUT_SUCCESS',
    // });
  };

  const systemSupportCompanyDataClean = () => {
    setSystemSupportData({});
    clearLocalStorage(SYSTEM_SUPPORT_COMPANY_DATA);
  };

  const systemSupportCompanyDataFetch = accessCode => {
    setSystemCompanyDataLoading(true);
    return axios
      .get(API_URL + `/atom-client/v1/company/public/${accessCode}/company-detail`, {
        headers: {
          'X-TIME-ZONE': getCurrentTimeZone(),
        },
      })
      .then(response => {
        setSystemCompanyDataLoading(false);
        if (response.status === 200) {
          setSystemSupportData(response.data.data);
          setLocalStorage(SYSTEM_SUPPORT_COMPANY_DATA, response.data.data);
          return response.data;
        }
      })
      .catch(error => {
        setSystemCompanyDataLoading(false);
        setSystemSupportData(error.response ? error.response.data.message : ERROR_MESSAGE);
      });
  };

  return (
    <SystemAuthContext.Provider
      {...props}
      value={{
        ...state,
        message: message,
        setMessage: setMessage,
        loading: loading,
        fields: fields,
        setLoading: setLoading,
        sideBarMenu: sideBarMenu,
        primaryColor: primaryColor,
        secondaryColor: secondaryColor,
        systemCompanyDataLoading: systemCompanyDataLoading,
        systemSupportCompanyDataFetch: systemSupportCompanyDataFetch,
        systemSupportCompanyDataClean: systemSupportCompanyDataClean,
        systemLogin: systemLogin,
        systemLogout: systemLogout,
        setFields: setFields,
        setPrimaryColor: setPrimaryColor,
        setSecondaryColor: setSecondaryColor,
        cleanSystemAuthContextClean: cleanSystemAuthContextClean,
        systemRequestCompanyEmailVerification: systemRequestCompanyEmailVerification,
        setUnreadSystemSupportCount: setUnreadSystemSupportCount,
      }}
    >
      {props.children}
    </SystemAuthContext.Provider>
  );
};

export { SystemAuthProvider, SystemAuthContext };
